// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: pure helpers for Choose project (state/AgentProjects.ts does the requests): which machines
// it lists, how recently each project was used (from the threads Konvo has listed), and paths.

import type {AgentMachineView, AgentProject, AgentThreadSummary} from '@konvo/schema/src/domains/konvo/Agents';

/** Machines Choose project lists: those that allow starting chats from Konvo, online or not. */
export function pickerMachines(machines: ReadonlyArray<AgentMachineView>): Array<AgentMachineView> {
	return machines.filter((machine) => machine.limits.send);
}

/** The machine's bridge can add, rename and remove projects (konvo-agents 0.22.0+). */
export function canManageProjects(machine: AgentMachineView | null | undefined): boolean {
	return Boolean(machine?.online && machine.limits.send && machine.tools[0]?.can.projects);
}

/** The machine's own GitHub login, when it has one (cloning needs it). */
export function githubOf(machine: AgentMachineView | null | undefined): {account?: string} | null {
	return machine?.tools[0]?.github ?? null;
}

export interface ProjectUse {
	/** The newest thread activity in the project. */
	last: string | null;
	/** Threads worked on today. */
	today: number;
	/** Threads the agent tool lists for it. */
	chats: number;
}

const dayStart = (now: number) => {
	const day = new Date(now);
	day.setHours(0, 0, 0, 0);
	return day.getTime();
};

/** How each project was used, from the machine's listed threads. */
export function projectUse(threads: ReadonlyArray<AgentThreadSummary> | null, now: number): Map<string, ProjectUse> {
	const uses = new Map<string, ProjectUse>();
	const today = dayStart(now);
	for (const thread of threads ?? []) {
		const use = uses.get(thread.projectId) ?? {last: null, today: 0, chats: 0};
		const at =
			thread.turnStartedAt && thread.turnStartedAt > thread.updatedAt ? thread.turnStartedAt : thread.updatedAt;
		use.chats++;
		if (!use.last || at > use.last) use.last = at;
		if (Date.parse(at) >= today) use.today++;
		uses.set(thread.projectId, use);
	}
	return uses;
}

/** Most recently used first, then by name. */
export function sortProjects(
	projects: ReadonlyArray<AgentProject>,
	uses: Map<string, ProjectUse>,
): Array<AgentProject> {
	return [...projects].sort((a, b) => {
		const al = uses.get(a.id)?.last ?? '';
		const bl = uses.get(b.id)?.last ?? '';
		if (al !== bl) return al < bl ? 1 : -1;
		return a.title.localeCompare(b.title, undefined, {sensitivity: 'base', numeric: true});
	});
}

export interface RecentProject {
	machine: AgentMachineView;
	project: AgentProject;
	use: ProjectUse;
}

/** The projects used last, across machines (online ones only: those can start a chat). */
export function recentProjects(
	entries: ReadonlyArray<{
		machine: AgentMachineView;
		projects: ReadonlyArray<AgentProject>;
		uses: Map<string, ProjectUse>;
	}>,
	max = 3,
): Array<RecentProject> {
	const all: Array<RecentProject> = [];
	for (const {machine, projects, uses} of entries) {
		if (!machine.online) continue;
		for (const project of projects) {
			const use = uses.get(project.id);
			if (use?.last) all.push({machine, project, use});
		}
	}
	return all.sort((a, b) => (a.use.last! < b.use.last! ? 1 : a.use.last! > b.use.last! ? -1 : 0)).slice(0, max);
}

/** /home/jp/code/konvo → ~/code/konvo when home is /home/jp. */
export function tildePath(path: string | undefined, home: string | null | undefined): string {
	if (!path) return '';
	if (!home) return path;
	if (path === home) return '~';
	const sep = home.includes('\\') ? '\\' : '/';
	return path.startsWith(home + sep) ? `~${sep}${path.slice(home.length + 1)}` : path;
}

/** Keeps the start and the end of a long path: ~/nuvio/…/icam-vizion-reports. */
export function shortPath(path: string, max = 44): string {
	if (path.length <= max) return path;
	const keep = max - 1;
	const head = Math.ceil(keep * 0.45);
	return `${path.slice(0, head)}…${path.slice(path.length - (keep - head))}`;
}

const parentOf = (path: string) => {
	const at = Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\'));
	return at > 0 ? path.slice(0, at) : null;
};

/** Where new projects most likely go: the folder most of the machine's projects sit in; else home (~). */
export function likelyParent(projects: ReadonlyArray<AgentProject>): string {
	const counts = new Map<string, number>();
	for (const project of projects) {
		const parent = project.path ? parentOf(project.path) : null;
		if (parent) counts.set(parent, (counts.get(parent) ?? 0) + 1);
	}
	let best: string | null = null;
	for (const [parent, count] of counts) if (!best || count > counts.get(best)!) best = parent;
	return best ?? '~';
}

/** A repository's folder name: owner/konvo-mobile → konvo-mobile. */
export const repoFolder = (repository: string) => repository.split('/').pop() ?? repository;

const HOME = /^(\/home\/[^/]+|\/Users\/[^/]+|[A-Za-z]:\\Users\\[^\\]+)(?=[\\/]|$)/;

/** The home folder most project paths sit under (/home/jp, /Users/jp, C:\Users\jp), until the machine says. */
export function guessHome(paths: ReadonlyArray<string | undefined>): string | null {
	const counts = new Map<string, number>();
	for (const path of paths) {
		const home = path ? HOME.exec(path)?.[1] : undefined;
		if (home) counts.set(home, (counts.get(home) ?? 0) + 1);
	}
	let best: string | null = null;
	for (const [home, count] of counts) if (!best || count > counts.get(best)!) best = home;
	return best;
}
